import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Fab,
  Menu,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Addchart as AddChartIcon,
  Compare as CompareIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./DashboardGrid.css";

import Widget from "./widgets/Widget";
import SuggestedWidgets from "./widgets/SuggestedWidgets";
import {
  useDashboard,
  useSaveDashboard,
  useWidgetTemplates,
} from "../hooks/useWidgets";
import { usePinDashboardWidget } from "../hooks/usePinDashboardWidget";
import { useGetMyFanProfileQuery } from "../store/api/mlbApi";
import { getDefaultDashboardLayout } from "../constants/defaultDashboardLayout";
import {
  applyGridLayoutChange,
  createWidgetId,
  ensureWidgetIds,
  layoutToGridItems,
} from "../utils/dashboardLayout";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../hooks/useAnalytics";

const ResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard = ({
  dashboardType = "home",
  isEditable = true,
  containerKey,
}) => {
  // Ensure dashboardType is defined before using hooks
  const effectiveDashboardType = dashboardType || "home";

  const { dashboard, isLoading, refetch } = useDashboard(
    effectiveDashboardType
  );
  const { saveUserDashboard, isLoading: isSaving } = useSaveDashboard(
    effectiveDashboardType
  );
  const { templates, isLoading: templatesLoading } = useWidgetTemplates(
    effectiveDashboardType
  );
  const { data: fanProfile, refetch: refetchFanProfile } =
    useGetMyFanProfileQuery(undefined, {
      skip: effectiveDashboardType !== "home",
    });
  const { pinWidget, isPinning } = usePinDashboardWidget();

  const navigate = useNavigate();

  // Analytics hook
  const {
    trackWidgetDeleted,
    trackWidgetCreationStarted,
    trackPhantomCardClicked,
    trackWidgetCreationOptionSelected,
    trackDashboardEditModeEntered,
  } = useAnalytics();

  const [layout, setLayout] = useState([]);
  const [gridConfig, setGridConfig] = useState({
    columns: 12,
    rowHeight: 100,
    margin: 10,
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [widgetTitle, setWidgetTitle] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalLayout, setOriginalLayout] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isPhantomCardOpen, setIsPhantomCardOpen] = useState(false);
  const [showTemplateSection, setShowTemplateSection] = useState(false);
  const hasSeededDefaultsRef = useRef(false);

  const defaultDashboard = getDefaultDashboardLayout(effectiveDashboardType);
  const defaultLayoutWithIds = useMemo(
    () => ensureWidgetIds(defaultDashboard.layout),
    [defaultDashboard.layout]
  );
  const displayLayout =
    layout.length > 0 ? layout : defaultLayoutWithIds;
  const activeLayout = layout.length > 0 ? layout : defaultLayoutWithIds;
  const isShowingStarterWidgets =
    displayLayout.length > 0 &&
    (layout.length === 0 || dashboard?.isDefaultLayout === true);

  const enterEditMode = useCallback(() => {
    const currentLayout = ensureWidgetIds(
      layout.length > 0 ? layout : [...displayLayout]
    );
    setLayout(currentLayout);
    setOriginalLayout(currentLayout.map((widget) => ({ ...widget })));
    setIsEditMode(true);
  }, [layout, displayLayout]);

  const gridLayout = useMemo(
    () => layoutToGridItems(activeLayout),
    [activeLayout]
  );

  // Initialize layout from dashboard data (skip while user is editing)
  useEffect(() => {
    if (dashboard && !isEditMode) {
      setLayout(ensureWidgetIds(dashboard.layout || []));
      setGridConfig(
        dashboard.gridConfig || defaultDashboard.gridConfig
      );
    }
  }, [dashboard, effectiveDashboardType, defaultDashboard.gridConfig, isEditMode]);

  // Recalculate grid width when parent container size changes (e.g. accordion expand)
  useEffect(() => {
    if (!containerKey) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timer);
  }, [containerKey]);

  // Persist starter widgets for users with empty saved layouts (client-side fallback)
  useEffect(() => {
    if (
      isLoading ||
      !dashboard ||
      hasSeededDefaultsRef.current ||
      (dashboard.layout && dashboard.layout.length > 0)
    ) {
      return;
    }

    hasSeededDefaultsRef.current = true;
    const { layout: starterLayout, gridConfig: starterGridConfig } =
      defaultDashboard;
    const seededLayout = ensureWidgetIds(starterLayout);

    setLayout(seededLayout);
    setGridConfig(starterGridConfig);

    saveUserDashboard(seededLayout, starterGridConfig).then((result) => {
      if (result.success) {
        refetch();
      }
    });
  }, [
    isLoading,
    dashboard,
    defaultDashboard,
    saveUserDashboard,
    refetch,
  ]);

  const handleGridLayoutChange = (newGridLayout) => {
    setLayout((current) => applyGridLayoutChange(current, newGridLayout));
  };

  const handleExitEditMode = async () => {
    const layoutToSave = ensureWidgetIds(layout);
    setLayout(layoutToSave);
    const result = await saveUserDashboard(layoutToSave, gridConfig);
    if (result.success) {
      refetch();
    } else {
      console.error("Failed to save layout:", result);
    }
    setIsEditMode(false);
  };

  const handleAddWidgetFromDialog = async () => {
    if (!selectedTemplate || !widgetTitle) return;

    const template = templates.find((t) => t._id === selectedTemplate);
    if (!template) return;

    // Determine appropriate size based on widget type
    let widgetSize = { width: 4, height: 2 };

    switch (template.config.type) {
      case "chart":
        widgetSize = { width: 6, height: 4 };
        break;
      case "table":
        widgetSize = { width: 8, height: 5 };
        break;
      case "metric":
        widgetSize = { width: 3, height: 2 };
        break;
      case "mlbScores":
        widgetSize = { width: 6, height: 6 };
        break;
      default:
        widgetSize = { width: 4, height: 2 };
    }

    const newWidget = {
      ...template.config,
      id: createWidgetId(),
      title: widgetTitle,
      position: { x: 0, y: layout.length },
      size: widgetSize,
    };

    const updatedLayout = [...layout, newWidget];
    setLayout(updatedLayout);

    if (!isEditMode) {
      const result = await saveUserDashboard(updatedLayout, gridConfig);
      if (result.success) {
        refetch();
      }
    }

    // Reset form
    setSelectedTemplate("");
    setWidgetTitle("");
    setIsAddDialogOpen(false);
  };

  const handleEditWidget = (_widgetId) => {
    // TODO: Implement widget editing
  };

  const handleDeleteWidget = (widgetId) => {
    const widget = layout.find((entry) => entry.id === widgetId);
    trackWidgetDeleted(widget?.type, widget?.title, effectiveDashboardType);

    const updatedLayout = layout.filter((entry) => entry.id !== widgetId);
    setLayout(updatedLayout);

    if (!isEditMode) {
      saveUserDashboard(updatedLayout, gridConfig).then((result) => {
        if (result.success) {
          refetch();
        }
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleSaveChanges = async () => {
    await handleExitEditMode();
    handleMenuClose();
  };

  const handleCancelChanges = () => {
    setLayout(originalLayout.map((widget) => ({ ...widget })));
    setIsEditMode(false);
    handleMenuClose();
  };

  const handleAddWidget = () => {
    // Track widget creation initiation
    trackWidgetCreationStarted("dashboard_fab", effectiveDashboardType);

    setIsAddDialogOpen(true);
    handleMenuClose();
  };

  const handlePhantomCardClick = () => {
    // Track phantom card interaction
    trackPhantomCardClicked(effectiveDashboardType, "empty_dashboard");

    setIsPhantomCardOpen(true);
  };

  const handleNavigateToPage = (page) => {
    setIsPhantomCardOpen(false);
    setIsAddDialogOpen(false);
    navigate(page);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pl: 0, pr: 0 }}>
      {/* Dashboard Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {isEditMode && (
            <Chip
              label="Edit Mode"
              color="warning"
              variant="filled"
              size="small"
            />
          )}
        </Box>

        <Box display="flex" gap={1}>
          {/* Edit mode controls are now in the floating action button menu */}
        </Box>
      </Box>

      {/* Dashboard Content */}
      {displayLayout.length === 0 ? (
        // Fallback when no widgets and no defaults available
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            p: 4,
          }}
        >
          <Box
            onClick={handlePhantomCardClick}
            sx={{
              width: 400,
              height: 300,
              border: 2,
              borderStyle: "dashed",
              borderColor: "divider",
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
                transform: "scale(1.02)",
              },
            }}
          >
            <AddChartIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No widgets yet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Click here to create your first widget
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box>
          {isShowingStarterWidgets && (
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Starter widgets to get you going — rearrange, edit, or replace
                them anytime.
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  trackDashboardEditModeEntered(
                    effectiveDashboardType,
                    displayLayout.length
                  );
                  enterEditMode();
                }}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              >
                Customize your dashboard
              </Link>
            </Box>
          )}
        <Box
          className={isEditMode ? "dashboard-edit-mode" : undefined}
          sx={{
            border: isEditMode ? 2 : 0,
            borderStyle: isEditMode ? "dashed" : "none",
            borderColor: isEditMode ? "divider" : "transparent",
            borderRadius: isEditMode ? 2 : 0,
            padding: isEditMode ? 1 : 0,
            backgroundColor: isEditMode ? "action.hover" : "transparent",
            minHeight: isEditMode ? 200 : "auto",
          }}
        >
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 0 }}
            cols={{ lg: gridConfig.columns || 12 }}
            rowHeight={gridConfig.rowHeight}
            margin={[gridConfig.margin, gridConfig.margin]}
            onDragStop={isEditMode ? handleGridLayoutChange : undefined}
            onResizeStop={isEditMode ? handleGridLayoutChange : undefined}
            isDraggable={isEditable && isEditMode}
            isResizable={isEditable && isEditMode}
            draggableHandle=".widget-drag-handle"
            compactType="vertical"
            preventCollision={false}
          >
            {activeLayout.map((widget) => (
              <Box
                key={widget.id}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  borderRadius: 4,
                }}
              >
                <Widget
                  config={widget}
                  onEdit={
                    isEditable && isEditMode
                      ? () => handleEditWidget(widget.id)
                      : null
                  }
                  onDelete={
                    isEditable && isEditMode
                      ? () => handleDeleteWidget(widget.id)
                      : null
                  }
                />
              </Box>
            ))}
          </ResponsiveGridLayout>
        </Box>
        </Box>
      )}

      {/* Add Widget Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Widget</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {effectiveDashboardType === "home" && (
              <SuggestedWidgets
                suggestions={fanProfile?.suggestions}
                disabled={isPinning}
                onPin={async (row) => {
                  const result = await pinWidget(row.widget);
                  if (result.success) {
                    refetch();
                    refetchFanProfile();
                    setIsAddDialogOpen(false);
                  }
                }}
              />
            )}
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Choose how you'd like to create your widget:
            </Typography>

            <Box
              onClick={() => handleNavigateToPage("/app/players")}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Single Player Chart</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a chart for a single player and save it as a widget
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => handleNavigateToPage("/app/compare")}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CompareIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Comparison Chart</Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare multiple players and save the chart as a widget
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => {
                // Show the template selection section
                setShowTemplateSection(true);
              }}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <AddChartIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Widget Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use pre-built widget templates
                </Typography>
              </Box>
            </Box>

            {/* Template Selection Section */}
            {showTemplateSection && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: 1,
                borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Select Template
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Widget Template</InputLabel>
                  <Select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    label="Widget Template"
                    disabled={templatesLoading}
                  >
                    {templatesLoading ? (
                      <MenuItem disabled>Loading templates...</MenuItem>
                    ) : templates.length === 0 ? (
                      <MenuItem disabled>No templates available</MenuItem>
                    ) : (
                      templates.map((template) => (
                        <MenuItem key={template._id} value={template._id}>
                          <Box>
                            <Typography variant="body1">
                              {template.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {template.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Widget Title"
                  value={widgetTitle}
                  onChange={(e) => setWidgetTitle(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <Box display="flex" gap={1}>
                  <Button
                    onClick={() => setShowTemplateSection(false)}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleAddWidgetFromDialog}
                    variant="contained"
                    disabled={!selectedTemplate || !widgetTitle}
                  >
                    Add Widget
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsAddDialogOpen(false);
              setShowTemplateSection(false);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phantom Card Dialog - New Widget Types */}
      <Dialog
        open={isPhantomCardOpen}
        onClose={() => setIsPhantomCardOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Your First Widget</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Choose how you'd like to create your widget:
            </Typography>

            <Box
              onClick={() => {
                // Track single player chart selection
                trackWidgetCreationOptionSelected(
                  "single_player_chart",
                  "phantom_card_dialog"
                );
                handleNavigateToPage("/app/players");
              }}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Single Player Chart</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a chart for a single player and save it as a widget
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => {
                // Track comparison chart selection
                trackWidgetCreationOptionSelected(
                  "comparison_chart",
                  "phantom_card_dialog"
                );
                handleNavigateToPage("/app/compare");
              }}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CompareIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Comparison Chart</Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare multiple players and save the chart as a widget
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => {
                // Track widget templates selection
                trackWidgetCreationOptionSelected(
                  "widget_templates",
                  "phantom_card_dialog"
                );
                setIsPhantomCardOpen(false);
                setIsAddDialogOpen(true);
              }}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <AddChartIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Box>
                <Typography variant="h6">Widget Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use pre-built widget templates
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPhantomCardOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button with Menu */}
      {isEditable && (
        <>
          <Fab
            color={isEditMode ? "error" : "primary"}
            aria-label="dashboard actions"
            onClick={handleMenuOpen}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
            }}
          >
            {isEditMode ? <CloseIcon /> : <MoreVertIcon />}
          </Fab>

          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            {!isEditMode ? (
              <>
                <MenuItem onClick={handleAddWidget}>
                  <AddIcon sx={{ mr: 1 }} />
                  Add Widget
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    trackDashboardEditModeEntered(
                      effectiveDashboardType,
                      displayLayout.length
                    );
                    enterEditMode();
                    handleMenuClose();
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} />
                  Edit
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem onClick={handleSaveChanges}>
                  <SaveIcon sx={{ mr: 1 }} />
                  Save Changes
                </MenuItem>
                <MenuItem onClick={handleCancelChanges}>
                  <CancelIcon sx={{ mr: 1 }} />
                  Cancel
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
